/**
 * Weiteres Gewerk unter derselben Nummer.
 *
 * Hausregel bei Eichenherz: Beschattung und Insektenschutz zum selben
 * Bauvorhaben laufen unter der Nummer des Fensterangebots. Kommen sie später
 * dazu – typischerweise Wochen danach – soll daraus kein neuer Vorgang werden,
 * sondern ein zweites Gewerk am bestehenden.
 *
 * Was dabei passiert, steht im Fenster, bevor man klickt: gleiche Nummer,
 * derselbe SharePoint-Ordner mit erweitertem Namen, ein eigenes Monday-Item.
 * Der Ordner wird umbenannt – das ist die Änderung, die man vorher wissen will.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import type { Angebot, AngebotAntwort } from '../typen';

export function GewerkModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: (neueId: number) => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const moeglich = angebot.ergaenzbareGewerke;
  const [prefix, setPrefix] = useState(moeglich[0] ?? '');
  const [fehler, setFehler] = useState('');

  const label = (p: string) => meta.kategorien.find((k) => k.prefix === p)?.label ?? p;

  const mutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebot.id}/gewerk`, { prefix }),
    onSuccess: (antwort) => {
      onFertig(antwort.angebot.id);
      if (antwort.toast) toastZeigen(antwort.toast);
      else if (antwort.sync.fehler) {
        toastZeigen('Gewerk angelegt – der Abgleich meldet ein Problem, siehe Angebot.');
      }
      onSchliessen();
    },
    onError: (f) =>
      setFehler(f instanceof Error ? f.message : 'Das Gewerk konnte nicht ergänzt werden.'),
  });

  /** So wird der Ordner nach dem Umbenennen heißen. */
  const neueKuerzel = [...angebot.gewerke, prefix]
    .filter((p, i, alle) => alle.indexOf(p) === i)
    .sort(
      (a, b) =>
        meta.kategorien.findIndex((k) => k.prefix === a) -
        meta.kategorien.findIndex((k) => k.prefix === b),
    )
    .join('+');

  const neuerName = `${angebot.kennung}_${neueKuerzel}_${angebot.kunde.kurz} (${angebot.kunde.ort})`;

  return (
    <Modal
      titel="Gewerk ergänzen"
      untertitel={`${angebot.kennung}_${angebot.prefix} · ${angebot.kunde.name}`}
      breite={520}
      abstandOben={70}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={() => mutation.mutate()} disabled={mutation.isPending || !prefix}>
            {mutation.isPending ? 'Wird angelegt …' : `${prefix} ergänzen`}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-feld">
        <span>Welches Gewerk kommt dazu?</span>
        <div className="gewerk-wahl">
          {moeglich.map((p) => (
            <button
              key={p}
              type="button"
              className={`gewerk-knopf ${prefix === p ? 'aktiv' : ''}`}
              onClick={() => {
                setPrefix(p);
                setFehler('');
              }}
            >
              <span className="gewerk-prefix">{p}</span>
              <span>{label(p)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Vorher zeigen, was danach anders heißt – der Ordner wird umbenannt. */}
      <div className="gewerk-vorschau">
        <div className="gewerk-vorschau-titel">Danach</div>
        <ul>
          <li>
            Gleiche Nummer <strong>{angebot.kennung}</strong>, ein zweiter Vorgang mit dem Gewerk{' '}
            <strong>{prefix}</strong>
          </li>
          <li>
            Derselbe SharePoint-Ordner, umbenannt auf <strong>{neuerName}</strong>
          </li>
          <li>Ein eigenes Monday-Item für {label(prefix)}</li>
        </ul>
      </div>

      <div className="modal-hinweis">
        Nur Beschattung und Insektenschutz dürfen unter einer bestehenden Nummer laufen, und nur an
        einem Fensterangebot. Alles andere bekommt eine eigene Nummer – dafür „Neues Angebot".
      </div>

      <div className="modal-hinweis">
        Das neue Gewerk startet bei „in Arbeit". Sobald es beim Kunden ist, dort auf „Angebot
        versendet" setzen – die Nachfass-Uhr läuft je Gewerk getrennt.
      </div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
